import { useEffect, useRef, useState } from 'react';

export default function TrustSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const testimonials = [
    {
      sport: 'プロ野球選手',
      position: '投手',
      quote: '試合後の肩の違和感が翌日にはほぼ引いていた。登板間隔の短い時期に助かっています。フィジオショットなしの遠征は考えられない。',
      initial: 'K',
    },
    {
      sport: 'Jリーガー',
      position: 'ミッドフィルダー',
      quote: '太腿の肉離れからの回復がこんなに早いのは初めての経験。医師にも驚かれた。毎試合後のルーティンになっています。',
      initial: 'T',
    },
    {
      sport: 'トップアスリート',
      position: 'マラソン選手',
      quote: '慢性的な足の違和感に悩んでいたが、継続施術で日常生活の痛みが激減。フルマラソンに復帰できたのはフィジオショットのおかげです。',
      initial: 'M',
    },
  ];

  const stats = [
    { value: '34', unit: '施設', label: '全国導入数' },
    { value: '2025', unit: '年5月', label: '日本上陸' },
    { value: '3', unit: '種目以上', label: 'プロ採用競技' },
  ];

  return (
    <section id="trust" ref={ref} className="py-16 md:py-24 bg-[#0A0C18] relative overflow-hidden">
      {/* Glow effects */}
      <div className="absolute bottom-0 left-1/3 w-80 h-80 rounded-full bg-[#1E6BFF]/6 blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        <div
          className={`mb-10 md:mb-14 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="h-0.5 w-10 bg-[#1E6BFF]" />
            <span className="text-[#5A9AFF] text-xs font-bold tracking-[0.25em] uppercase">Trust &amp; Track Record</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#E8EFFF] mb-4 leading-tight">
            プロが認めた。<br />
            <span className="bg-gradient-to-r from-[#5A9AFF] to-[#1E6BFF] bg-clip-text text-transparent">日本が動いている。</span>
          </h2>
          <p className="text-[#C0CBDD] text-base md:text-lg max-w-2xl leading-relaxed">
            「効く」と言うのは簡単。でも、プロアスリートが自らの身体で証明した事実は重い。
            彼らに妥協はない。だから、この声は本物だ。
          </p>
        </div>

        {/* Stats */}
        <div
          className={`grid grid-cols-3 gap-3 md:gap-5 mb-10 md:mb-14 transition-all duration-700 delay-100 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {stats.map((stat) => (
            <div key={stat.label} className="bg-[#0D1020] border border-[#1A2545] rounded-xl p-4 md:p-6 text-center hover:border-[#1E6BFF]/40 hover:shadow-[0_0_20px_rgba(30,107,255,0.1)] transition-all">
              <div className="flex items-baseline justify-center gap-1 mb-1">
                <span className="text-2xl md:text-4xl font-black bg-gradient-to-b from-[#5A9AFF] to-[#1E6BFF] bg-clip-text text-transparent">{stat.value}</span>
                <span className="text-[#5A9AFF] text-sm md:text-base font-bold">{stat.unit}</span>
              </div>
              <p className="text-[#D0DDF0] text-xs md:text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Banner image */}
        <div className="relative rounded-2xl overflow-hidden mb-10 md:mb-14 w-full h-48 md:h-72 border border-[#1A2545]">
          <img
            src="https://public.readdy.ai/ai/img_res/b229ed6d5206295279410d11da4bffd3.jpg"
            alt="プロアスリートの実績"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050608]/90 via-[#050608]/50 to-[#050608]/70" />
          {/* Blue shimmer overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E6BFF]/15 via-transparent to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <p className="text-[#5A9AFF] text-xs font-bold tracking-[0.3em] uppercase mb-2">Used By</p>
              <h3 className="text-white font-black text-2xl md:text-4xl">プロ野球選手・Jリーガー</h3>
              <p className="text-[#C0CBDD] text-sm md:text-base mt-2">トップアスリートが選んだリカバリーテクノロジー</p>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div
          className={`grid md:grid-cols-3 gap-4 md:gap-6 transition-all duration-700 delay-300 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {testimonials.map((t, i) => (
            <div
              key={i}
              className="bg-[#0D1020] border border-[#1A2545] hover:border-[#1E6BFF]/40 rounded-xl p-6 transition-all hover:shadow-[0_0_20px_rgba(30,107,255,0.08)]"
            >
              <div className="flex items-center gap-1 text-[#1E6BFF] mb-4">
                {[...Array(5)].map((_, j) => (
                  <div key={j} className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-xs" />
                  </div>
                ))}
              </div>
              <p className="text-[#C0CBDD] text-sm leading-relaxed mb-5">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="flex items-center gap-3 border-t border-[#1A2545] pt-4">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-b from-[#2A7FFF] to-[#0D4FCC] rounded-full shrink-0 shadow-[0_0_10px_rgba(30,107,255,0.4)]">
                  <span className="text-white font-black text-sm">{t.initial}</span>
                </div>
                <div>
                  <p className="text-[#E8EFFF] font-bold text-sm">{t.sport}</p>
                  <p className="text-[#D0DDF0] text-xs">{t.position}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
